(() => {
  if (customElements.get('gold-price-ticker')) return;
  const DAY = 24 * 60 * 60 * 1000;
  const memory = new Map();
  const pending = new Map();
  const numeric = (value) => typeof value === 'number' && Number.isFinite(value);

  class GoldPriceTicker extends HTMLElement {
    connectedCallback() {
      this.primary = this.querySelector('[data-primary]');
      this.track = this.querySelector('.price-ticker__track');
      this.viewport = this.querySelector('.price-ticker__viewport');
      this.motionPreference = matchMedia('(prefers-reduced-motion: reduce)');
      this.onMotionChange = () => this.repeat();
      this.onFocusChange = () => this.syncPlayback();
      this.motionPreference.addEventListener('change', this.onMotionChange);
      this.viewport.addEventListener('focusin', this.onFocusChange);
      this.viewport.addEventListener('focusout', this.onFocusChange);
      this.currency = ['USD', 'EUR', 'GBP', 'AUD', 'CAD'].includes(this.dataset.currency) ? this.dataset.currency : 'USD';
      this.formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: this.currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });
      this.onVisibility = () => {
        this.syncPlayback();
        if (!document.hidden) this.refresh();
      };
      document.addEventListener('visibilitychange', this.onVisibility);
      this.observer = new ResizeObserver(() => this.repeat());
      this.observer.observe(this.querySelector('.price-ticker__viewport'));
      this.observer.observe(this.primary);
      this.refresh();
      this.timer = setInterval(() => { if (!document.hidden) this.refresh(); }, 60000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      this.observer?.disconnect();
      this.animation?.cancel();
      this.animation = null;
      this.motionPreference?.removeEventListener('change', this.onMotionChange);
      this.viewport?.removeEventListener('focusin', this.onFocusChange);
      this.viewport?.removeEventListener('focusout', this.onFocusChange);
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    repeat() {
      const width = this.primary.getBoundingClientRect().width;
      if (!width) {
        this.animation?.cancel();
        this.animation = null;
        this.track.querySelectorAll('[data-copy]').forEach((node) => node.remove());
        return;
      }
      // Keep at least one full cycle beyond the viewport, including at the seam.
      const count = this.motionPreference.matches ? 0 : Math.ceil(this.viewport.clientWidth / width);
      const markup = this.primary.innerHTML;
      if (markup !== this.copyMarkup || count !== this.copyCount) {
        this.track.querySelectorAll('[data-copy]').forEach((node) => node.remove());
        const copies = document.createDocumentFragment();
        for (let i = 0; i < count; i++) {
          const copy = this.primary.cloneNode(true);
          copy.removeAttribute('data-primary');
          copy.setAttribute('data-copy', '');
          copy.setAttribute('aria-hidden', 'true');
          copy.querySelectorAll('[data-shopify-editor-block]').forEach((node) => node.removeAttribute('data-shopify-editor-block'));
          copies.append(copy);
        }
        this.track.append(copies);
        this.copyMarkup = markup;
        this.copyCount = count;
      }
      this.updateAnimation(width);
    }

    updateAnimation(width) {
      if (this.motionPreference.matches) {
        this.animation?.cancel();
        this.animation = null;
        return;
      }
      const speed = Math.min(200, Math.max(10, Number(this.dataset.scrollSpeed) || 40));
      if (this.animation && this.cycleWidth === width && this.scrollSpeed === speed) return;
      // Preserve the travelled pixel offset when text width or speed changes.
      const offset = this.animation
        ? ((Number(this.animation.currentTime) || 0) * this.scrollSpeed / 1000) % this.cycleWidth
        : 0;
      this.animation?.cancel();
      this.cycleWidth = width;
      this.scrollSpeed = speed;
      this.animation = this.track.animate([
        { transform: 'translate3d(0, 0, 0)' },
        { transform: `translate3d(${-width}px, 0, 0)` }
      ], { duration: width / speed * 1000, iterations: Infinity, easing: 'linear' });
      this.animation.currentTime = (offset % width) / speed * 1000;
      this.syncPlayback();
    }

    syncPlayback() {
      if (!this.animation) return;
      if (document.hidden || this.viewport.matches(':focus-within')) this.animation.pause();
      else if (this.animation.playState !== 'running') this.animation.play();
    }

    async quote(metal) {
      // Keep cached prices separate from the previous provider.
      const key = `gold-price-ticker:cloudflare:v1:${this.dataset.proxyUrl || 'direct'}:${metal}:${this.currency}`;
      let cached = memory.get(key);
      if (!cached) {
        try { cached = JSON.parse(localStorage.getItem(key)); } catch (_) { /* Storage may be blocked. */ }
      }
      const now = Date.now();
      if (cached && numeric(cached.price) && numeric(cached.priceAt) && cached.priceAt <= now && now - (cached.fetchedAt || cached.priceAt) < (this.dataset.proxyUrl ? 60000 : DAY)) return cached;
      if (pending.has(key)) return pending.get(key);
      const request = (async () => {
        try {
          const url = this.dataset.proxyUrl ? new URL(this.dataset.proxyUrl) : new URL(`https://api.gold-api.com/price/${encodeURIComponent(metal)}/${this.currency}`);
          if (this.dataset.proxyUrl) {
            url.pathname = '/quote';
            url.searchParams.set('metal', metal);
            url.searchParams.set('currency', this.currency);
          }
          const headers = { Accept: 'application/json' };
          const response = await fetch(url, { headers, signal: AbortSignal.timeout(15000), cache: 'no-store' });
          if (!response.ok) throw new Error('Price request failed');
          const data = await response.json();
          if (!numeric(data.price) || data.price <= 0 || data.error) throw new Error('Invalid price');
          if (data.symbol !== metal || (data.currency && data.currency !== this.currency)) throw new Error('Unexpected quote');
          const updatedAt = typeof data.updatedAt === 'number' ? data.updatedAt : Date.parse(data.updatedAt);
          const result = {
            price: data.price, priceAt: numeric(data.priceAt) ? data.priceAt : now, fetchedAt: now,
            updatedAt: Number.isFinite(updatedAt) ? updatedAt : now,
            change: this.dataset.proxyUrl && numeric(data.change) ? data.change : null,
            changeAt: data.changeAt, changeFrom: data.changeFrom, changeTo: data.changeTo,
            stale: Boolean(data.stale), changeStale: Boolean(data.changeStale)
          };
          memory.set(key, result);
          try { localStorage.setItem(key, JSON.stringify(result)); } catch (_) { /* Use memory cache. */ }
          return result;
        } catch (_) {
          if (cached && numeric(cached.price)) return { ...cached, stale: true };
          return null;
        } finally {
          pending.delete(key);
        }
      })();
      pending.set(key, request);
      return request;
    }

    manualQuote(item) {
      const parse = (value) => /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test((value || '').trim()) ? Number(value) : NaN;
      const price = parse(item.dataset.manualPrice);
      const change = parse(item.dataset.manualChange);
      return numeric(price) && price >= 0 && numeric(change) ? { price, change, manual: true } : null;
    }

    async refresh() {
      if (this.loading) return;
      this.loading = true;
      try {
        const items = Array.from(this.primary.querySelectorAll('[data-metal]'));
        const results = await Promise.all(items.map(async (item) => {
          const manual = item.dataset.manual === 'true';
          const quote = manual ? this.manualQuote(item) : await this.quote(item.dataset.metal);
          if (!this.isConnected) return quote;
          item.querySelector('[data-price]').textContent = quote ? this.formatter.format(quote.price) : '—';
          item.title = quote ? `Giá / troy ounce · ${this.currency} · ${new Date(quote.updatedAt || quote.priceAt).toLocaleString()}${quote.stale ? ' · Chưa cập nhật được giá mới' : ''}` : 'Chưa lấy được giá';
          if (manual) item.title = quote ? 'Giá thủ công / troy ounce · ' + this.currency : 'Giá thủ công không hợp lệ';
          const change = item.querySelector('[data-change]');
          if (change) {
            const value = quote?.change;
            change.dataset.direction = numeric(value) ? (value > 0 ? 'up' : value < 0 ? 'down' : 'flat') : 'flat';
            change.textContent = numeric(value) ? `${value > 0 ? '▲ +' : value < 0 ? '▼ −' : ''}${Math.abs(value).toFixed(2)}%` : '—';
            change.title = numeric(value)
              ? `Biến động USD trong 24 giờ trước lần cập nhật · Từ ${new Date(quote.changeFrom).toLocaleString()} đến ${new Date(quote.changeTo || quote.changeAt).toLocaleString()} · Cập nhật mỗi ngày`
              : 'Chưa lấy được chỉ số thay đổi so với ngày trước';
          }
            if (manual && change) change.title = quote ? 'Chỉ số thay đổi được nhập thủ công' : 'Chỉ số thủ công không hợp lệ';
          this.repeat();
          return quote;
        }));
        if (!this.isConnected) return;
        const failed = results.some((quote) => !quote || quote.stale);
        this.message(failed ? 'Một số giá chưa cập nhật được. Giá cũ được giữ kèm thời điểm cập nhật.' : '', failed ? 'Không lấy được một số giá. Vui lòng thử lại sau.' : 'Giá cập nhật hằng ngày lúc 00:00 (UTC+7). % biến động USD so với 24 giờ trước từ gold-api.com.');
        const manualCount = items.filter((item) => item.dataset.manual === 'true').length;
        if (manualCount) {
          const note = this.querySelector('[data-editor]');
          if (note) note.textContent = items.some((item) => item.dataset.manual === 'true' && !this.manualQuote(item))
            ? 'Giá thủ công không hợp lệ. Dùng số và dấu chấm thập phân, giá không âm.'
            : manualCount + '/' + items.length + ' block đang dùng giá và chỉ số thủ công.';
        }
        this.repeat();
      } finally {
        this.loading = false;
      }
    }

    message(status, editor) {
      this.querySelector('[data-status]').textContent = status;
      const note = this.querySelector('[data-editor]');
      if (note) note.textContent = editor;
    }
  }
  customElements.define('gold-price-ticker', GoldPriceTicker);
})();
